import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { NgbPaginationConfig } from '@ng-bootstrap/ng-bootstrap';
import { DsColors, DsPageMessageVariants, Icons, IconScale, IconStyles } from '@levelaccess/design-system';

import {
  IDclConfig,
  ITableConfig,
  ITableEmptyState,
  ITableRow,
  SortEvent,
  TableEmptyStateType,
} from '../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import {
  IPageSummary,
  IPageSummaryServerResponse,
  ISummaryRequestQuery,
} from '../../../../../../../shared/interfaces/scan-summaries.interface';
import { NgbTableUtilities } from '../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { $scanSummaries } from '../../../../../../../shared/constants/scan-summaries';
import { Api, ApiQueryOption } from '../../../../../../../shared/constants/api';
import { $scanAudit } from '../../../../../../../shared/constants/scan-audit';
import { AccessibilityAuditToolNames, AuditToolNameToAuditType } from '../../../../../../../shared/constants/audit-tool';
import { IScanAudit } from '../../../../../../../shared/interfaces/scan-audit.interface';
import { DEFAULT_ENTRY_LIMITS, DEFAULT_PAGINATION_MAX_SIZE } from '../../../../../constants/form.constants';
import { TranslateService } from '../../../../../translate/translate.service';
import { PageSummariesService } from '../../../../../services/page-summaries.service';
import { AuditTypes } from '../../../../../../../shared/constants/audit-types';
import { IAudit } from '../../../../../../../shared/interfaces/audit.interface';
import { $pageColumns } from '../../../../../shared/constants';
import { ILinkCellConfig } from '../../../../../components/table/ngb-table/cells/link-cell/link-cell.component';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';
import { RestService } from '../../../../../services/rest.service';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';

@Component({
  selector: 'app-view-scan-results-pages',
  templateUrl: './view-scan-results-pages.component.html',
  styleUrls: ['./view-scan-results-pages.component.scss'],
  providers: [NgbPaginationConfig],
  standalone: false,
})
export class ViewScanResultsPagesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public scanId: string;
  @Input() public workspaceId: string;
  @Input() public digitalPropertyId: string;
  @Input() public scanAudit: IScanAudit;
  @Input() public auditTool: AccessibilityAuditToolNames;

  public getPages$: BehaviorSubject<ITableRow[]> = new BehaviorSubject<ITableRow[]>(undefined);
  public pagesResult$: Observable<ITableRow[]>;

  public DEFAULT_ENTRY_LIMITS: typeof DEFAULT_ENTRY_LIMITS = DEFAULT_ENTRY_LIMITS;
  public entriesPerPage: number = DEFAULT_ENTRY_LIMITS[1];
  public page: number = 1;
  public urlQueryTerm: string = '';
  public totalPages: number = 0;
  public sortEvent: SortEvent = null;
  public tableConfig: ITableConfig;

  private subscription: Subscription;
  private useDsPageMessage: boolean;
  private hasScreenshots: boolean;

  constructor(
    private translateService: TranslateService,
    private pageSummariesService: PageSummariesService,
    private ngbPaginationConfig: NgbPaginationConfig,
    private featureFlagService: FeatureFlagService,
    private restService: RestService,
  ) {
    this.ngbPaginationConfig.maxSize = DEFAULT_PAGINATION_MAX_SIZE;
    this.subscription = new Subscription();
    this.hasScreenshots = false;
    this.useDsPageMessage = this.featureFlagService.variation(FeatureFlagCollection.dsPageMessage, false);
  }

  private toTableRow(page: IPageSummary): ITableRow {
    const documentId: string = page[$scanSummaries.documentId];
    const audit: IAudit = this.scanAudit[$scanAudit.audits][AuditToolNameToAuditType[this.auditTool]];

    const row: ITableRow = {
      data: {
        [$pageColumns.url]: NgbTableUtilities.linkCell({
          text: page[$scanSummaries.url],
          attributes: {
            href: `/${Api.scans}/${Api.scan_results}/${this.scanId}/${Api.single_report}/${AuditToolNameToAuditType[this.auditTool]}/${documentId}`,
          },
          styles: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }),
        [$pageColumns.score]: NgbTableUtilities.scoreCell({
          score: Math.floor(page[$scanSummaries.score]),
        }),
        [$pageColumns.open]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getOpenIssues(page, audit)),
        }),
        [$pageColumns.dismissed]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getDismissedIssues(page, audit)),
        }),
        [$pageColumns.totalFailed]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getTotalFailedIssues(page, audit)),
        }),
        [$pageColumns.totalReview]: NgbTableUtilities.textCell({
          text: String(this.pageSummariesService.getTotalReviewIssues(page, audit)),
        }),
      },
    };

    const isScreenshotEnabled: boolean = this.scanAudit?.[$scanAudit.audits]?.[AuditTypes.screenshots]?.isSelectedForScanning;
    if (isScreenshotEnabled) {
      const emulatorLink: IDclConfig<ILinkCellConfig> = NgbTableUtilities.linkCell({
        text: this.translateService.instant('emulator'),
        icon: {
          iconName: 'eye',
          iconWidth: '1rem',
          iconHeight: '1rem',
          classes: ['emulator-icon'],
        },
        attributes: {
          href: `/${Api.scans}/${Api.scan_results}/${this.scanId}/${Api.single_report}/${AuditTypes.screenshots}/${documentId}`,
        },
      });
      row.data[$pageColumns.action] = emulatorLink;
    }

    const screenshotUploadId: string | undefined = page[$scanSummaries.screenshotUploadId];
    const screenshotThumbnailUploadId: string | undefined = page[$scanSummaries.screenshotThumbnailUploadId];
    if (SharedCommonUtility.notNullish(screenshotUploadId) && SharedCommonUtility.notNullish(screenshotThumbnailUploadId)) {
      const fullSizeUrl: string = this.restService.getResourceMoreSecureViewUrl(screenshotUploadId);
      const thumbnailUrl: string = this.restService.getResourceMoreSecureViewUrl(screenshotThumbnailUploadId);
      row.data[$pageColumns.screenshot] = NgbTableUtilities.attachmentsCell({
        attachments: [
          {
            iconId: 'attachment-picture',
            href: fullSizeUrl,
            thumbnailSrc: thumbnailUrl,
            // isInternal: false bypasses the Angular download-link interceptor so the image
            // opens in a new tab instead of being intercepted as an Angular navigation event.
            isInternal: false,
            isImage: true,
            altText: this.translateService.instant('screenshot'),
          },
        ],
      });
    }

    return row;
  }

  private getQueryParams(): ISummaryRequestQuery {
    const query: ISummaryRequestQuery = {
      [ApiQueryOption.toolName]: this.auditTool,
      limit: String(this.entriesPerPage),
      page: String(this.page),
      url: this.urlQueryTerm,
    };

    if (this.sortEvent !== null) {
      query.sortBy = this.sortEvent.column;
      query.sortDirection = this.sortEvent.direction;
    }

    return query;
  }

  private setup(): void {
    this.tableConfig = this.getTableConfig();
    this.pagesResult$ = this.getPages$.pipe(
      switchMap((): Observable<IPageSummaryServerResponse> => {
        return this.pageSummariesService.getPageSummariesForTool(
          this.workspaceId,
          this.digitalPropertyId,
          this.scanId,
          this.getQueryParams(),
        );
      }),
      switchMap((response: IPageSummaryServerResponse): Observable<ITableRow[]> => {
        this.totalPages = response.total;
        if (
          !this.hasScreenshots &&
          response.summaries.some(
            (s: IPageSummary) =>
              SharedCommonUtility.notNullish(s[$scanSummaries.screenshotUploadId]) &&
              SharedCommonUtility.notNullish(s[$scanSummaries.screenshotThumbnailUploadId]),
          )
        ) {
          this.hasScreenshots = true;
          this.tableConfig = this.getTableConfig();
        }
        return of(response.summaries.map(this.toTableRow.bind(this)));
      }),
    );
  }

  public getTableConfig(): ITableConfig {
    const emptyStateTitle: string = this.translateService.instant('no_search_results_available');
    const emptyStateSubtitle: string = this.translateService.instant('no_search_results_available_description');
    const emptyState: ITableEmptyState = this.useDsPageMessage
      ? {
          type: TableEmptyStateType.dsPageMessage,
          title: emptyStateTitle,
          subtitle: emptyStateSubtitle,
          dsPageMessage: {
            icon: Icons.NotFoundBrand,
            iconColor: DsColors.Primary,
            iconScale: IconScale.FA_4X,
            iconStyle: IconStyles.Light,
            variant: DsPageMessageVariants.info,
          },
        }
      : {
          iconId: 'magnifying-glass-2',
          title: emptyStateTitle,
          subtitle: emptyStateSubtitle,
        };

    const tableConfig: ITableConfig = {
      columns: {
        [$pageColumns.url]: {
          translationKey: 'label_url',
          sortingEnabled: true,
          styles: {
            maxWidth: '14rem',
            width: '50%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        [$pageColumns.score]: {
          translationKey: 'page_score',
          sortingEnabled: true,
        },
        [$pageColumns.open]: {
          translationKey: 'open',
        },
        [$pageColumns.dismissed]: {
          translationKey: 'dismissed',
        },
        [$pageColumns.totalFailed]: {
          translationKey: 'tab_failed',
        },
        [$pageColumns.totalReview]: {
          translationKey: 'label_requires_review',
        },
      },
      caption: this.translateService.instant('scan_results_pages_table_caption'),
      emptyState,
    };

    const isScreenshotEnabled: boolean = this.scanAudit?.[$scanAudit.audits]?.[AuditTypes.screenshots]?.isSelectedForScanning;
    if (isScreenshotEnabled) {
      tableConfig.columns[$pageColumns.action] = {
        translationKey: 'column_header_action',
      };
    }
    if (this.hasScreenshots) {
      tableConfig.columns[$pageColumns.screenshot] = { translationKey: 'screenshot' };
    }
    return tableConfig;
  }

  public getPaginationLabel(page: number, entriesPerPage: number, total: number): string {
    return this.translateService.instant('n_of_t', [
      `${Math.min((page - 1) * entriesPerPage + 1, total)} - ${Math.min(page * entriesPerPage, total)}`,
      String(total),
    ]);
  }

  public onTableSort(sortEvent: SortEvent): void {
    this.sortEvent = sortEvent;
    this.getPages$.next(undefined);
  }

  public ngOnInit(): void {
    this.setup();
    this.subscription.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.dsPageMessage, false)
        .pipe(map(Boolean), startWith(this.useDsPageMessage))
        .subscribe((useDsPageMessage: boolean): void => {
          this.useDsPageMessage = useDsPageMessage;
          this.tableConfig = this.getTableConfig();
        }),
    );
  }

  public ngOnChanges(_changes: SimpleChanges): void {
    this.hasScreenshots = false;
    this.tableConfig = this.getTableConfig();
    this.getPages$.next(undefined);
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
